import { reactive, readonly } from 'vue'

/**
 * One rAF loop, one resize listener, one pointer listener for the whole app.
 * Every parallax effect on the page reads from this single store, so scrolling
 * never triggers more than one layout read per frame.
 */
const state = reactive({
  scrollY: 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 900,
  width: typeof window !== 'undefined' ? window.innerWidth : 1440,
  docHeight: 1,
  progress: 0, // 0..1 through the whole document
  pointerX: 0, // -1 .. 1, eased
  pointerY: 0,
  coarse: false,
  reduced: false
})

let started = false
let targetPX = 0
let targetPY = 0

function measure() {
  state.height = window.innerHeight
  state.width = window.innerWidth
  state.docHeight = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
}

function frame() {
  state.scrollY = window.scrollY || window.pageYOffset || 0
  state.progress = Math.min(1, Math.max(0, state.scrollY / state.docHeight))
  // ease the pointer so tilt feels weighted rather than twitchy
  state.pointerX += (targetPX - state.pointerX) * 0.06
  state.pointerY += (targetPY - state.pointerY) * 0.06
  requestAnimationFrame(frame)
}

export function startViewport() {
  if (started || typeof window === 'undefined') return
  started = true
  state.coarse = window.matchMedia('(pointer: coarse)').matches
  state.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  measure()
  window.addEventListener('resize', measure, { passive: true })
  window.addEventListener('load', measure)
  setInterval(measure, 1500) // catch late layout shifts (fonts, images)
  if (!state.coarse) {
    window.addEventListener(
      'pointermove',
      (e) => {
        targetPX = (e.clientX / window.innerWidth) * 2 - 1
        targetPY = (e.clientY / window.innerHeight) * 2 - 1
      },
      { passive: true }
    )
  }
  requestAnimationFrame(frame)
}

export function useViewport() {
  return readonly(state)
}

/**
 * How far a given element has travelled through the viewport.
 * 0 = its top edge is just entering from the bottom
 * 1 = its bottom edge has just left the top
 */
export function elementProgress(rectTop, rectHeight, viewportH) {
  const total = rectHeight + viewportH
  const travelled = viewportH - rectTop
  return Math.min(1, Math.max(0, travelled / total))
}
